import React, { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import StatusBadge from './StatusBadge';
import { getAvailableActions } from '../../lib/billStatus';

/**
 * 날짜 칸 라벨.
 *
 * "발급완료 일자" 라고 적혀 있으면 이미 완료된 날짜를 보여주는 것처럼 읽힌다.
 * 실제로는 "이 날짜로 처리하겠다" 는 입력값이다.
 * 위 상세 화면에 쓰는 이름과 똑같이 맞춰서 어느 값을 채우는지 바로 알게 한다.
 */
const DATE_LABEL = {
  reqCompDt: '발행일',
  procCompDt: '수금/지급일',
  rsltCompDt: '실적확정일',
};

// 현재 status 에서 가능한 액션만 버튼으로 노출 (disabled 산재 로직 대체)
const StatusActionPanel = ({ bill, onSubmit, submitting, approvalEnabled, canCancelIssue }) => {
  const actions = getAvailableActions(bill, { approvalEnabled, canCancelIssue });
  const [dates, setDates] = useState({});

  const dateFor = (action) => dates[action.key] || dayjs();

  return (
    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">현재 상태</Typography>
        <StatusBadge bill={bill} />
      </Stack>
      {actions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          가능한 처리가 없습니다.
        </Typography>
      ) : (
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          {actions.map((action) => (
            <Stack key={action.key} direction="row" spacing={1} alignItems="center">
              {action.dateField && (
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
                  <DatePicker
                    label={DATE_LABEL[action.dateField] || '처리일자'}
                    format="YYYY-MM-DD"
                    value={dateFor(action)}
                    onChange={(v) =>
                      setDates((d) => ({ ...d, [action.key]: v }))
                    }
                    // v6 에서 renderInput 이 없어졌다. 그대로 두면 size 가 무시돼 칸만 커진다.
                    slotProps={{
                      textField: { size: 'small', helperText: '이 날짜로 기록됩니다' },
                    }}
                  />
                </LocalizationProvider>
              )}
              <Button
                variant={action.kind === 'cancel' ? 'outlined' : 'contained'}
                color={action.kind === 'cancel' ? 'warning' : 'primary'}
                // 처리 중 연타를 막는다. 같은 요청이 두 번 가면 결과를 종잡을 수 없다.
                // requiresAuth 는 권한이 없어 막힌 것 — 눌러도 서버가 거부한다.
                disabled={submitting || action.requiresAuth}
                title={action.requiresAuth ? '권한자만 할 수 있습니다' : undefined}
                onClick={() => {
                  const extra = action.dateField
                    ? {
                        [action.dateField]: dayjs(dateFor(action)).format(
                          'YYYY-MM-DD',
                        ),
                      }
                    : {};
                  onSubmit(action, extra);
                }}
              >
                {action.label}
              </Button>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default StatusActionPanel;
